/**
 * Bookings Types for Booking Management View
 *
 * Extended types for the bookings list/search view separate from calendar.
 * Supports filtering, searching, sorting, and analytics.
 */

import type { BookingStatus, PaymentStatus } from './calendar';
import { BOOKING_STATUS_COLORS, PAYMENT_STATUS_COLORS } from './calendar';

// Re-export for convenience
export type { BookingStatus, PaymentStatus };
export { BOOKING_STATUS_COLORS, PAYMENT_STATUS_COLORS };

// ============================================================================
// Constants
// ============================================================================

/** Milliseconds per day for date calculations */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ============================================================================
// Booking Source (where booking originated)
// ============================================================================

export type BookingSource = 'WHATSAPP' | 'WEB' | 'MANUAL' | 'GOOGLE' | 'PHONE' | 'WALK_IN';

export const BOOKING_SOURCE_LABELS: Record<BookingSource, string> = {
  WHATSAPP: 'WhatsApp',
  WEB: 'Online',
  MANUAL: 'Manual',
  GOOGLE: 'Google',
  PHONE: 'Phone',
  WALK_IN: 'Walk-in',
};

export const BOOKING_SOURCE_ICONS: Record<BookingSource, string> = {
  WHATSAPP: '\uD83D\uDCAC',
  WEB: '\uD83C\uDF10',
  MANUAL: '\u270F\uFE0F',
  GOOGLE: '\uD83D\uDCC5',
  PHONE: '\uD83D\uDCDE',
  WALK_IN: '\uD83D\uDEB6',
};

// ============================================================================
// Date Range Presets
// ============================================================================

export type DateRangePreset =
  | 'all_time'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'last_30_days'
  | 'last_90_days'
  | 'custom';

export const DATE_RANGE_LABELS: Record<DateRangePreset, string> = {
  all_time: 'All Time',
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This Week',
  last_week: 'Last Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  last_30_days: 'Last 30 Days',
  last_90_days: 'Last 90 Days',
  custom: 'Custom Range',
};

// ============================================================================
// Filter Interfaces
// ============================================================================

/**
 * Comprehensive booking filters for search/list view
 */
export interface BookingFilters {
  /** Text search - customer name, phone, email */
  searchQuery: string;

  /** Date range filter */
  dateRange: {
    preset: DateRangePreset;
    startDate: Date | null;
    endDate: Date | null;
  };

  /** Booking status filter (multi-select) */
  statuses: BookingStatus[];

  /** Payment status filter (multi-select) */
  paymentStatuses: PaymentStatus[];

  /** Business filter (for platform admins) */
  businessId: string | null;

  /** Staff member filter */
  staffId: string | null;

  /** Service filter */
  serviceId: string | null;

  /** Booking source filter */
  sources: BookingSource[];

  /** Sort configuration */
  sortBy: BookingSortField;
  sortOrder: 'asc' | 'desc';
}

export type BookingSortField =
  | 'scheduledAt'
  | 'createdAt'
  | 'customerName'
  | 'serviceName'
  | 'status'
  | 'paymentStatus'
  | 'price';

/**
 * Default filter values
 */
export const DEFAULT_BOOKING_FILTERS: BookingFilters = {
  searchQuery: '',
  dateRange: {
    preset: 'all_time',
    startDate: null,
    endDate: null,
  },
  statuses: [],
  paymentStatuses: [],
  businessId: null,
  staffId: null,
  serviceId: null,
  sources: [],
  sortBy: 'scheduledAt',
  sortOrder: 'desc',
};

// ============================================================================
// Booking List Item (for table display)
// ============================================================================

/**
 * Booking data structured for list/table display
 */
export interface BookingListItem {
  id: string;

  // Business info
  businessId: string;
  businessName: string;

  // Customer info
  customerId?: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;

  // Service info
  serviceId: string;
  serviceName: string;
  servicePrice: number;
  serviceCurrency: string;

  // Staff info
  staffId?: string;
  staffName?: string;

  // Scheduling
  scheduledAt: Date;
  endsAt: Date;
  durationMinutes: number;

  // Status
  status: BookingStatus;
  paymentStatus: PaymentStatus;

  // Source
  source: BookingSource;

  // Pricing
  totalPrice: number;
  depositAmount?: number;
  tipAmount?: number;

  // Notes
  notes?: string;
  cancellationReason?: string;

  // Metadata
  createdAt: Date;
  updatedAt?: Date;

  // Related data
  linkedOrderId?: string;

  // Linked payment ID (for direct navigation to payment details)
  paymentId?: string;
}

// ============================================================================
// Booking Stats
// ============================================================================

/**
 * Aggregated statistics for filtered bookings
 */
export interface BookingStats {
  /** Total count of filtered bookings */
  totalCount: number;

  /** Count by status */
  byStatus: Record<BookingStatus, number>;

  /** Count by payment status */
  byPaymentStatus: Record<PaymentStatus, number>;

  /** Revenue totals */
  totalRevenue: number;
  paidRevenue: number;
  pendingRevenue: number;

  /** Rates */
  completionRate: number; // completed / (completed + cancelled + no_show)
  noShowRate: number; // no_show / total
  cancellationRate: number; // cancelled / total
}

/**
 * Calculate stats from a list of bookings
 */
export function calculateBookingStats(bookings: BookingListItem[]): BookingStats {
  const stats: BookingStats = {
    totalCount: bookings.length,
    byStatus: {
      PENDING: 0,
      CONFIRMED: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      CANCELLED: 0,
      NO_SHOW: 0,
    },
    byPaymentStatus: {
      PENDING: 0,
      PAID: 0,
      FAILED: 0,
      REFUNDED: 0,
      WAIVED: 0,
    },
    totalRevenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
    completionRate: 0,
    noShowRate: 0,
    cancellationRate: 0,
  };

  bookings.forEach((booking) => {
    stats.byStatus[booking.status]++;
    stats.byPaymentStatus[booking.paymentStatus]++;
    stats.totalRevenue += booking.totalPrice;

    if (booking.paymentStatus === 'PAID') {
      stats.paidRevenue += booking.totalPrice;
    } else if (booking.paymentStatus === 'PENDING') {
      stats.pendingRevenue += booking.totalPrice;
    }
  });

  // Calculate rates
  const finishedBookings =
    stats.byStatus.COMPLETED + stats.byStatus.CANCELLED + stats.byStatus.NO_SHOW;

  if (finishedBookings > 0) {
    stats.completionRate = stats.byStatus.COMPLETED / finishedBookings;
  }

  if (stats.totalCount > 0) {
    stats.noShowRate = stats.byStatus.NO_SHOW / stats.totalCount;
    stats.cancellationRate = stats.byStatus.CANCELLED / stats.totalCount;
  }

  return stats;
}

// ============================================================================
// Pagination
// ============================================================================

export interface PaginationState {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export const DEFAULT_PAGINATION: PaginationState = {
  page: 1,
  pageSize: 20,
  totalItems: 0,
  totalPages: 0,
};

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// ============================================================================
// Booking Actions
// ============================================================================

export type BookingAction =
  | 'view'
  | 'confirm'
  | 'start'
  | 'complete'
  | 'cancel'
  | 'no_show'
  | 'reschedule'
  | 'message'
  | 'refund'
  | 'copy_id';

/**
 * Get available actions based on booking status
 */
export function getAvailableActions(booking: BookingListItem): BookingAction[] {
  const actions: BookingAction[] = ['view', 'message', 'copy_id'];

  switch (booking.status) {
    case 'PENDING':
      return ['confirm', 'cancel', ...actions];
    case 'CONFIRMED':
      return ['start', 'reschedule', 'cancel', 'no_show', ...actions];
    case 'IN_PROGRESS':
      return ['complete', 'no_show', ...actions];
    case 'COMPLETED':
      if (booking.paymentStatus === 'PAID') {
        return ['refund', ...actions];
      }
      return actions;
    case 'CANCELLED':
    case 'NO_SHOW':
    default:
      return actions;
  }
}

/**
 * Action button configuration
 */
export interface ActionConfig {
  label: string;
  icon: string;
  variant: 'primary' | 'secondary' | 'danger' | 'success';
}

export const ACTION_CONFIGS: Record<BookingAction, ActionConfig> = {
  view: { label: 'View Details', icon: '\uD83D\uDC41\uFE0F', variant: 'secondary' },
  confirm: { label: 'Confirm', icon: '\u2713', variant: 'success' },
  start: { label: 'Start Service', icon: '\u25B6\uFE0F', variant: 'primary' },
  complete: { label: 'Complete', icon: '\u2713', variant: 'success' },
  cancel: { label: 'Cancel', icon: '\u2715', variant: 'danger' },
  no_show: { label: 'Mark No-Show', icon: '\uD83D\uDEAB', variant: 'danger' },
  reschedule: { label: 'Reschedule', icon: '\uD83D\uDCC5', variant: 'secondary' },
  message: { label: 'Send Message', icon: '\uD83D\uDCAC', variant: 'secondary' },
  refund: { label: 'Refund', icon: '\u21A9\uFE0F', variant: 'danger' },
  copy_id: { label: 'Copy ID', icon: '\uD83D\uDCCB', variant: 'secondary' },
};

// ============================================================================
// Export Configuration
// ============================================================================

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ExportColumn {
  key: string;
  label: string;
  selected: boolean;
}

export const EXPORTABLE_COLUMNS: ExportColumn[] = [
  { key: 'id', label: 'Booking ID', selected: true },
  { key: 'businessName', label: 'Business', selected: true },
  { key: 'customerName', label: 'Customer Name', selected: true },
  { key: 'customerPhone', label: 'Phone', selected: true },
  { key: 'customerEmail', label: 'Email', selected: false },
  { key: 'serviceName', label: 'Service', selected: true },
  { key: 'staffName', label: 'Staff', selected: true },
  { key: 'scheduledAt', label: 'Date/Time', selected: true },
  { key: 'durationMinutes', label: 'Duration (min)', selected: true },
  { key: 'totalPrice', label: 'Price', selected: true },
  { key: 'status', label: 'Status', selected: true },
  { key: 'paymentStatus', label: 'Payment Status', selected: true },
  { key: 'source', label: 'Booking Source', selected: false },
  { key: 'notes', label: 'Notes', selected: false },
  { key: 'createdAt', label: 'Created At', selected: false },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get date range from preset
 */
export function getDateRangeFromPreset(preset: DateRangePreset): { start: Date; end: Date } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'all_time':
      return {
        start: new Date(2020, 0, 1),
        end: new Date(now.getFullYear() + 1, 11, 31),
      };
    case 'today':
      return {
        start: today,
        end: new Date(today.getTime() + MS_PER_DAY - 1),
      };
    case 'yesterday': {
      const yesterday = new Date(today.getTime() - MS_PER_DAY);
      return {
        start: yesterday,
        end: new Date(today.getTime() - 1),
      };
    }
    case 'this_week': {
      const dayOfWeek = today.getDay();
      const monday = new Date(today.getTime() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) * MS_PER_DAY);
      return {
        start: monday,
        end: now,
      };
    }
    case 'last_week': {
      const dayOfWeek = today.getDay();
      const lastMonday = new Date(
        today.getTime() - (dayOfWeek === 0 ? 13 : dayOfWeek + 6) * MS_PER_DAY
      );
      const lastSunday = new Date(lastMonday.getTime() + 7 * MS_PER_DAY - 1);
      return {
        start: lastMonday,
        end: lastSunday,
      };
    }
    case 'this_month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: now,
      };
    case 'last_month': {
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        start: lastMonth,
        end: lastDayOfLastMonth,
      };
    }
    case 'last_30_days':
      return {
        start: new Date(now.getTime() - 30 * MS_PER_DAY),
        end: now,
      };
    case 'last_90_days':
      return {
        start: new Date(now.getTime() - 90 * MS_PER_DAY),
        end: now,
      };
    case 'custom':
    default:
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: now,
      };
  }
}

// Re-export from centralized utilities to avoid duplication
export { formatDuration, formatCurrency } from '@/lib/utils/date';

/**
 * Format date for display with relative time hints (Today, Tomorrow, Yesterday)
 */
export function formatBookingDate(date: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const bookingDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffDays = Math.round((bookingDate.getTime() - today.getTime()) / MS_PER_DAY);

  if (diffDays === 0) {
    return `Today, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  } else if (diffDays === 1) {
    return `Tomorrow, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  } else if (diffDays === -1) {
    return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
}
